import { useEffect, useRef, useState } from "react";
import { Crosshair, Loader2, MapPin, X } from "lucide-react";
import { api } from "@/lib/api";
import { GOOGLE_ENABLED, placeDetails, placesSuggest } from "@/lib/mapProvider";

const suggest = async (q) => {
  if (GOOGLE_ENABLED) {
    try { return await placesSuggest(q); } catch { /* fall back to the backend geocoder */ }
  }
  return (await api.get("/geo/search", { params: { q } })).data.results;
};

const LocateButton = ({ testId, locating, onClick }) => (
  <button type="button" data-testid={`${testId}-use-location`} onClick={onClick} disabled={locating}
    className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-300 transition-colors hover:text-amber-200 disabled:opacity-60">
    {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />} Use my location
  </button>
);

export const AddressInput = ({ label, value, onChange, onRemove, testId, placeholder, locate = false }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const skip = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    const q = value.text.trim();
    if (q.length < 3 || value.point) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await suggest(q);
        setResults(rows);
        setOpen(true);
        onChange({ ...value, error: rows.length ? "" : "Address not found" });
      } catch {
        onChange({ ...value, error: "Geocoder unavailable, try again" });
      } finally {
        setLoading(false);
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.text]);

  const pick = async (r) => {
    skip.current = true;
    setOpen(false);
    if (r.place_id && r.lat == null) {
      try { r = await placeDetails(r.place_id); } catch {
        const { data } = await api.get("/geo/search", { params: { q: r.address } }).catch(() => ({ data: { results: [] } }));
        if (!data.results[0]) { onChange({ text: r.address, point: null, error: "Address not found" }); return; }
        r = data.results[0];
      }
    }
    onChange({ text: r.address, point: r, error: "" });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { onChange({ ...value, error: "Location unavailable — type your pickup instead" }); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        const { data } = await api.get("/geo/reverse", { params: { lat: p.coords.latitude, lng: p.coords.longitude } });
        pick(data);
      } catch {
        onChange({ ...value, error: "Couldn't look up your address — type your pickup instead" });
      }
      setLocating(false);
    }, (e) => {
      setLocating(false);
      const error = e.code === 1 ? "Location access denied — turn on location to book" : "Couldn't get your location — try again or type your pickup";
      onChange({ ...valueRef.current, error });
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
  };

  return (
    <div className="relative">
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="lux-eyebrow block">{label}</label>
        {locate && <LocateButton testId={testId} locating={locating} onClick={useMyLocation} />}
      </div>
      <div className="relative flex items-center">
        <MapPin className={`pointer-events-none absolute left-3 h-4 w-4 ${value.point ? "text-amber-400" : "text-zinc-500"}`} />
        <input data-testid={testId} className="lux-input !pl-9 !pr-10" placeholder={placeholder} value={value.text}
          onChange={(e) => onChange({ text: e.target.value, point: null, error: "" })}
          onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)} />
        {loading && <Loader2 className="absolute right-3 h-4 w-4 animate-spin text-amber-400" />}
        {!loading && onRemove && (
          <button type="button" data-testid={`${testId}-remove`} onClick={onRemove} className="absolute right-2 rounded-full p-1.5 text-zinc-500 hover:text-red-300" aria-label="Remove stop">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {value.error && <p data-testid={`${testId}-error`} className="mt-1.5 text-xs font-medium text-amber-500">{value.error}</p>}
      {open && results.length > 0 && (
        <ul data-testid={`${testId}-suggestions`} className="absolute z-[1200] mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-amber-500/20 bg-[#121216]/95 shadow-2xl backdrop-blur-xl">
          {results.map((r, i) => (
            <li key={i}>
              <button type="button" data-testid={`${testId}-suggestion-${i}`} onMouseDown={() => pick(r)} className="w-full px-4 py-2.5 text-left text-sm text-zinc-300 transition-colors hover:bg-amber-400/10 hover:text-amber-100">
                {r.address}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
